import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Pagination } from 'react-bootstrap';
import ApiService from 'helpers/apiService';
import PairsTable from './PairsTable';
import { ICrossPair, IProvider } from './types';

const PAGE_SIZE = 50;

// Лига кросс-пары = конкретная пара конкретного провайдера (идентифицируется pairId).
// Бэкенд (CrossRelation.PairId) хранит именно pairId — раньше форма слала 0 и теряла
// идентичность источника. Один символ может быть у нескольких провайдеров → разные лиги.
interface Leg {
  pairId: number;
  symbol: string;
  provider: string;
}

// Текущий курс по символу (перенесено из вкладки «Доступные валюты»): цена,
// статус актуальности, время получения. Матчится к кросс-паре по symbol.
interface RateInfo { price: number | string; valid?: boolean; created_at: string }

// Сортируемые столбцы таблицы кросс-курсов.
type SortKey = 'id' | 'symbol' | 'active' | 'price' | 'valid' | 'received' | 'deviation_limit' | 'discrepancy_limit';

const Pairs = () => {
  const [loading, setLoading] = useState(true);
  const [pairs, setPairs] = useState<ICrossPair[]>([]);
  const [ratesBySymbol, setRatesBySymbol] = useState<Record<string, RateInfo>>({});
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('symbol');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Данные для формы добавления
  const [legs, setLegs] = useState<Leg[]>([]);
  const [legsLoading, setLegsLoading] = useState(true);
  const [legsError, setLegsError] = useState<string | null>(null);

  const [selectedBase, setSelectedBase] = useState('');
  const [selectedQuote, setSelectedQuote] = useState('');
  const [deviationLimit, setDeviationLimit] = useState('');
  const [discrepancyLimit, setDiscrepancyLimit] = useState('');
  const [reliableLeg, setReliableLeg] = useState<number | ''>('');
  const [correctiveLegs, setCorrectiveLegs] = useState<number[]>([]);
  const [stabilizerLegs, setStabilizerLegs] = useState<number[]>([]);
  const [correctivePick, setCorrectivePick] = useState<number | ''>('');
  const [stabilizerPick, setStabilizerPick] = useState<number | ''>('');
  const [relatedOnly, setRelatedOnly] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    fetchPairs();
    fetchLegs();
  }, []);

  const fetchPairs = async () => {
    let loadedPairs: ICrossPair[] = [];
    try {
      const response = await ApiService.getCrossPairs<{ data: ICrossPair[] }>();
      loadedPairs = response.data ?? [];
      setPairs(loadedPairs);
      setCurrentPage(1);
    } catch (error) {
      console.error('Ошибка загрузки кросс-пар:', error);
      setPairs([]);
    } finally {
      setLoading(false);
    }
    // Курсы для колонок «Курс/Статус/Получен». Backend GetRate резолвит cross_rate
    // ПО ПЕРЕДАННЫМ символам (пустой список → пусто!), поэтому запрашиваем именно
    // символы загруженных кросс-пар. Берём последнюю запись по символу (max created_at).
    const symbols = Array.from(new Set(
      loadedPairs.map(p => (p.symbol || '').replace(/"/g, '').trim()).filter(Boolean)
    ));
    if (symbols.length === 0) { setRatesBySymbol({}); return; }
    try {
      const rates = await ApiService.listRates<Array<{ symbol: string; price: number | string; valid?: boolean; created_at: string }>>({ symbol: symbols.join(',') });
      const map: Record<string, RateInfo> = {};
      (rates || []).forEach((r) => {
        const sym = (r.symbol || '').replace(/"/g, '').trim().toUpperCase();
        if (!sym) return;
        const prev = map[sym];
        if (!prev || new Date(r.created_at).getTime() >= new Date(prev.created_at).getTime()) {
          map[sym] = { price: r.price, valid: r.valid, created_at: r.created_at };
        }
      });
      setRatesBySymbol(map);
    } catch (e) {
      console.warn('Ошибка загрузки курсов для таблицы кросс-курсов:', e);
    }
  };

  // Загружаем доступные пары (лиги) ОДИН раз: провайдеры → пары провайдера (с id).
  const fetchLegs = async () => {
    setLegsLoading(true);
    setLegsError(null);
    try {
      const providersResponse = await ApiService.getProviders<{ data: IProvider[] }>({});
      const providers = providersResponse.data ?? [];
      const collected: Leg[] = [];
      for (const provider of providers) {
        const ratesResponse = await ApiService.getRates<{ data: Array<{ id: number; symbol: string; enable_history: boolean }> }>(
          provider.id
        );
        const rates = ratesResponse.data ?? [];
        rates
          .filter(p => p.enable_history && p.id)
          .forEach(p => {
            const symbol = (p.symbol || '').replace(/"/g, '').trim().toUpperCase();
            if (symbol.includes('/')) {
              collected.push({ pairId: p.id, symbol, provider: provider.name });
            }
          });
      }
      setLegs(collected);
      if (collected.length === 0) {
        setLegsError('Не удалось получить пары провайдеров (пустой список). Проверьте провайдеров и доступность сервиса курсов.');
      }
    } catch (e) {
      console.error('Ошибка загрузки пар провайдеров:', e);
      setLegsError('Ошибка загрузки пар провайдеров. Подробности в консоли (возможно, CORS/недоступность сервиса курсов).');
      setLegs([]);
    } finally {
      setLegsLoading(false);
    }
  };

  // Список валют (из символов лиг) для выбора base/quote — оба свободно из всех валют
  // (кросс-пара по определению может НЕ иметь прямой пары base/quote).
  const currencies = useMemo(() => {
    const set = new Set<string>();
    legs.forEach(l => {
      const [b, q] = l.symbol.split('/');
      if (b) set.add(b);
      if (q) set.add(q);
    });
    return Array.from(set).sort();
  }, [legs]);

  const legById = useMemo(() => {
    const m = new Map<number, Leg>();
    legs.forEach(l => m.set(l.pairId, l));
    return m;
  }, [legs]);

  // Опции лиг: по умолчанию только связанные с base/quote (полезно для кросса),
  // иначе — все. Исключаем уже выбранные в других полях.
  const legOptions = useMemo(() => {
    const base = selectedBase.toUpperCase();
    const quote = selectedQuote.toUpperCase();
    const isRelated = (l: Leg) => {
      const [b, q] = l.symbol.split('/');
      return (base && (b === base || q === base)) || (quote && (b === quote || q === quote));
    };
    return legs
      .filter(l => (relatedOnly ? isRelated(l) : true))
      .sort((a, b) => a.symbol.localeCompare(b.symbol) || a.provider.localeCompare(b.provider));
  }, [legs, selectedBase, selectedQuote, relatedOnly]);

  const optionLabel = (l: Leg) => `${l.symbol} · ${l.provider}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormMsg(null);
    const base = selectedBase.trim().toUpperCase();
    const quote = selectedQuote.trim().toUpperCase();
    if (!base || !quote) {
      setFormMsg({ type: 'err', text: 'Выберите базовую и котируемую валюту.' });
      return;
    }
    if (base === quote) {
      setFormMsg({ type: 'err', text: 'Базовая и котируемая валюта совпадают.' });
      return;
    }
    if (reliableLeg === '') {
      setFormMsg({ type: 'err', text: 'Выберите надёжный источник.' });
      return;
    }

    const toRelation = (pairId: number) => {
      const leg = legById.get(pairId);
      return { pair_id: pairId, type: leg?.provider || '', status: 'active' };
    };

    const payload = {
      symbol: `${base}/${quote}`,
      discrepancy_limit: Number(discrepancyLimit) || 0,
      deviation_limit: Number(deviationLimit) || 0,
      active: true,
      reliable_source: toRelation(reliableLeg),
      corrective_rates: correctiveLegs.map(toRelation),
      stabilizer_rates: stabilizerLegs.map(toRelation)
    };

    setSubmitting(true);
    try {
      await ApiService.addCrossPairs(payload);
      setFormMsg({ type: 'ok', text: `Кросс-пара ${payload.symbol} добавлена.` });
      setSelectedBase('');
      setSelectedQuote('');
      setDeviationLimit('');
      setDiscrepancyLimit('');
      setReliableLeg('');
      setCorrectiveLegs([]);
      setStabilizerLegs([]);
      setCorrectivePick('');
      setStabilizerPick('');
      fetchPairs();
    } catch (error) {
      console.error('Ошибка при добавлении кросс-пары:', error);
      setFormMsg({ type: 'err', text: 'Ошибка при добавлении кросс-пары (см. консоль).' });
    } finally {
      setSubmitting(false);
    }
  };

  // Фильтр (активные + поиск) → сортировка по выбранному столбцу → пагинация.
  const filteredPairs = useMemo(() => {
    const q = search.trim().toUpperCase();
    let list = showOnlyActive ? pairs.filter(p => p.active) : pairs.slice();
    if (q) {
      list = list.filter(p => (p.symbol || '').toUpperCase().includes(q));
    }
    const rate = (sym: string) => ratesBySymbol[(sym || '').replace(/"/g, '').trim().toUpperCase()];
    const val = (p: ICrossPair): number | string | boolean => {
      switch (sortKey) {
        case 'id': return p.id;
        case 'symbol': return (p.symbol || '').toUpperCase();
        case 'active': return p.active ? 1 : 0;
        case 'deviation_limit': return p.deviation_limit ?? 0;
        case 'discrepancy_limit': return p.discrepancy_limit ?? 0;
        case 'price': { const r = rate(p.symbol); return r ? Number(r.price) || 0 : -Infinity; }
        case 'valid': { const r = rate(p.symbol); return r ? (r.valid ? 1 : 0) : -1; }
        case 'received': { const r = rate(p.symbol); return r ? new Date(r.created_at).getTime() || 0 : -Infinity; }
        default: return 0;
      }
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return list;
  }, [pairs, showOnlyActive, search, sortKey, sortDir, ratesBySymbol]);

  const handleSort = useCallback((keyStr: string) => {
    const key = keyStr as SortKey;
    setSortKey(prev => {
      if (prev === key) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); return prev; }
      setSortDir('asc');
      return key;
    });
    setCurrentPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(filteredPairs.length / PAGE_SIZE));
  const paginatedPairs = useMemo(
    () => filteredPairs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredPairs, currentPage]
  );
  const activeCount = useMemo(() => pairs.filter(p => p.active).length, [pairs]);
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [showOnlyActive, search]);

  const chip = (pairId: number, onRemove: () => void) => {
    const leg = legById.get(pairId);
    return (
      <span key={pairId} className="badge bg-light text-dark border me-1 mb-1">
        {leg ? optionLabel(leg) : pairId}
        <button type="button" className="btn-close btn-close-sm ms-2" style={{ fontSize: '0.6rem' }} onClick={onRemove} />
      </span>
    );
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Управление кросс-парами</h4>
          <span className="badge bg-success">Активных: {activeCount}</span>
          <span className="badge bg-secondary">Всего: {pairs.length}</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <input
            type="search"
            className="form-control form-control-sm"
            style={{ width: 200 }}
            placeholder="Поиск по паре (BTC/USD)"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <label className="form-check form-switch m-0">
            <input
              type="checkbox"
              className="form-check-input"
              checked={showOnlyActive}
              onChange={e => setShowOnlyActive(e.target.checked)}
            />
            <span className="form-check-label small">Только активные</span>
          </label>
          <button className="btn btn-primary" onClick={fetchPairs}>Обновить данные</button>
        </div>
      </Card.Header>
      <Card.Body>
        {!loading && (
          <div className="mb-2 text-muted small">
            Показано {filteredPairs.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredPairs.length)} из {filteredPairs.length}
            {showOnlyActive && pairs.length > activeCount && (
              <span className="ms-2">(скрыто неактивных: {pairs.length - activeCount})</span>
            )}
          </div>
        )}
        <PairsTable
          data={paginatedPairs}
          loading={loading}
          legs={legs}
          ratesBySymbol={ratesBySymbol}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          onRefresh={fetchPairs}
        />
        {!loading && totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <Pagination className="mb-0">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
              <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
              <Pagination.Item active>{currentPage}</Pagination.Item>
              <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages} />
              <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage >= totalPages} />
            </Pagination>
            <span className="text-muted">Страница {currentPage} из {totalPages}</span>
          </div>
        )}

        {/* Форма добавления кросс-пары */}
        <hr className="my-4" />
        <h5 className="mb-3">Добавить кросс-пару</h5>

        {legsLoading && <div className="text-muted small mb-2">Загрузка пар провайдеров…</div>}
        {legsError && (
          <div className="alert alert-warning py-2 small d-flex justify-content-between align-items-center">
            <span>{legsError}</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchLegs}>Повторить</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            {/* Символ кросс-пары */}
            <div className="col-md-4">
              <label className="form-label small">Кросс-пара (база / котировка)</label>
              <div className="d-flex align-items-center gap-2">
                <select value={selectedBase} onChange={e => setSelectedBase(e.target.value)} className="form-select form-select-sm">
                  <option value="">база…</option>
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <span>/</span>
                <select value={selectedQuote} onChange={e => setSelectedQuote(e.target.value)} className="form-select form-select-sm">
                  <option value="">котировка…</option>
                  {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-text">Можно собрать кросс из пар, даже если прямой пары base/quote нет.</div>
            </div>

            <div className="col-md-2">
              <label className="form-label small">Лимит отклонения</label>
              <input type="number" step="any" className="form-control form-control-sm" value={deviationLimit} onChange={e => setDeviationLimit(e.target.value)} placeholder="0" />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Лимит расхождения</label>
              <input type="number" step="any" className="form-control form-control-sm" value={discrepancyLimit} onChange={e => setDiscrepancyLimit(e.target.value)} placeholder="0" />
            </div>

            <div className="col-md-4 d-flex align-items-end">
              <label className="form-check form-switch m-0">
                <input type="checkbox" className="form-check-input" checked={relatedOnly} onChange={e => setRelatedOnly(e.target.checked)} />
                <span className="form-check-label small">Показывать только пары, связанные с base/quote</span>
              </label>
            </div>

            {/* Надёжный источник */}
            <div className="col-md-4">
              <label className="form-label small">Надёжный источник *</label>
              <select
                className="form-select form-select-sm"
                value={reliableLeg}
                onChange={e => setReliableLeg(e.target.value ? Number(e.target.value) : '')}
                disabled={legsLoading}
              >
                <option value="">— выберите пару —</option>
                {legOptions
                  .filter(l => !correctiveLegs.includes(l.pairId) && !stabilizerLegs.includes(l.pairId))
                  .map(l => <option key={l.pairId} value={l.pairId}>{optionLabel(l)}</option>)}
              </select>
            </div>

            {/* Корректирующие */}
            <div className="col-md-4">
              <label className="form-label small">Корректирующие котировки</label>
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm"
                  value={correctivePick}
                  onChange={e => setCorrectivePick(e.target.value ? Number(e.target.value) : '')}
                  disabled={legsLoading}
                >
                  <option value="">— добавить пару —</option>
                  {legOptions
                    .filter(l => l.pairId !== reliableLeg && !correctiveLegs.includes(l.pairId) && !stabilizerLegs.includes(l.pairId))
                    .map(l => <option key={l.pairId} value={l.pairId}>{optionLabel(l)}</option>)}
                </select>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={correctivePick === ''}
                  onClick={() => {
                    if (correctivePick !== '') {
                      setCorrectiveLegs(prev => [...prev, correctivePick]);
                      setCorrectivePick('');
                    }
                  }}
                >+</button>
              </div>
              <div className="mt-1">{correctiveLegs.map(id => chip(id, () => setCorrectiveLegs(prev => prev.filter(x => x !== id))))}</div>
            </div>

            {/* Стабилизирующие */}
            <div className="col-md-4">
              <label className="form-label small">Стабилизирующие котировки</label>
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm"
                  value={stabilizerPick}
                  onChange={e => setStabilizerPick(e.target.value ? Number(e.target.value) : '')}
                  disabled={legsLoading}
                >
                  <option value="">— добавить пару —</option>
                  {legOptions
                    .filter(l => l.pairId !== reliableLeg && !correctiveLegs.includes(l.pairId) && !stabilizerLegs.includes(l.pairId))
                    .map(l => <option key={l.pairId} value={l.pairId}>{optionLabel(l)}</option>)}
                </select>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={stabilizerPick === ''}
                  onClick={() => {
                    if (stabilizerPick !== '') {
                      setStabilizerLegs(prev => [...prev, stabilizerPick]);
                      setStabilizerPick('');
                    }
                  }}
                >+</button>
              </div>
              <div className="mt-1">{stabilizerLegs.map(id => chip(id, () => setStabilizerLegs(prev => prev.filter(x => x !== id))))}</div>
            </div>

            <div className="col-12 d-flex align-items-center gap-3">
              <button type="submit" className="btn btn-success btn-sm" disabled={submitting || legsLoading}>
                {submitting ? 'Добавление…' : 'Добавить кросс-пару'}
              </button>
              {formMsg && (
                <span className={`small ${formMsg.type === 'ok' ? 'text-success' : 'text-danger'}`}>{formMsg.text}</span>
              )}
            </div>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
};

export default Pairs;
